<script setup lang="ts">
import type { FormSubmitEvent, FormErrorEvent } from '@nuxt/ui';
import { contactSchema, CONTACT_SUBJECT_OPTIONS, type ContactFormData } from '@/schemas/contact';
import { mapApiErrors } from '@/utils/mapApiErrors';

const props = defineProps<{ subject?: string; isSignup?: boolean }>();

const state = reactive<Partial<ContactFormData>>({ subject: props.subject as ContactFormData['subject'] });
const alert = ref<{ text: string } | null>(null);
const isSuccess = ref(false);
const form = ref();

const { useCreateSupportMessageMutation } = useHaru();
const contactMutation = useCreateSupportMessageMutation();

const subjectOptions = CONTACT_SUBJECT_OPTIONS.map(s => ({ label: s, value: s }));

async function onSubmit(event: FormSubmitEvent<ContactFormData>) {
  alert.value = null;

  try {
    await contactMutation.mutateAsync({ body: { ...event.data, phone_number: event.data.phone_number ?? '' } });

    isSuccess.value = true;
  }
  catch (err: unknown) {
    const error = err as { detail?: Array<{ loc: string[]; msg: string; type: string }> };
    const apiErrors = mapApiErrors(error?.detail);

    if (Object.keys(apiErrors).length > 0) {
      const formErrors = Object.entries(apiErrors).map(([name, message]) => ({ name, message }));
      form.value?.setErrors(formErrors);
    }
    else {
      alert.value = {
        text: 'Din besked kunne ikke sendes. Prøv igen, eller kontakt os direkte på telefon.',
      };
    }
  }
}

function onError(event: FormErrorEvent) {
  const firstError = event.errors?.[0];
  if (firstError?.id) {
    document.getElementById(firstError.id)?.focus();
  }
}
</script>

<template>
  <!-- Success state -->
  <div
    v-if="isSuccess"
    class="flex flex-col items-center justify-center gap-4 py-12 text-center"
  >
    <div class="size-16 rounded-full bg-primary-50 flex items-center justify-center">
      <UIcon
        name="i-lucide-circle-check"
        class="text-primary-600 size-10"
      />
    </div>

    <h2 class="text-2xl font-bold">
      Tak for din besked
    </h2>

    <p class="text-sm w-3/4 text-[var(--color-charcoal)]/60 leading-relaxed">
      Vi har modtaget din henvendelse og svarer hurtigst muligt — typisk inden for 1-2 hverdage.
    </p>
  </div>

  <!-- Form -->
  <UForm
    v-else
    ref="form"
    :schema="contactSchema"
    :state="state"
    :loading-auto="false"
    class="space-y-5"
    @submit="onSubmit"
    @error="onError"
  >
    <UAlert
      v-if="alert"
      color="error"
      :title="alert.text"
      icon="i-lucide-alert-circle"
    />

    <!-- Personal info section -->
    <h2
      v-if="isSignup"
      class="text-2xl font-bold mb-2"
    >
      Tilmeld dig her
    </h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <UFormField
        name="first_name"
        label="Fornavn"
        required
      >
        <UInput
          v-model="state.first_name"
          placeholder="Dit fornavn"
          autocomplete="given-name"
        />
      </UFormField>

      <UFormField
        name="last_name"
        label="Efternavn"
        required
      >
        <UInput
          v-model="state.last_name"
          placeholder="Dit efternavn"
          autocomplete="family-name"
        />
      </UFormField>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <UFormField
        name="email"
        label="Email"
        required
      >
        <UInput
          v-model="state.email"
          type="email"
          inputmode="email"
          :spellcheck="false"
          placeholder="din@email.dk"
          autocomplete="email"
        />
      </UFormField>

      <UFormField
        name="phone_number"
        label="Mobil"
      >
        <UInput
          v-model="state.phone_number"
          type="tel"
          inputmode="tel"
          placeholder="Dit mobilnummer"
          autocomplete="tel"
        />
      </UFormField>
    </div>

    <!-- Company section (optional) -->
    <p class="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoal)]/55 pt-5 pb-1 border-t border-[var(--color-warm-border)]/40">
      Virksomhed <span class="font-normal normal-case tracking-normal">(valgfrit)</span>
    </p>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <UFormField
        name="company"
        label="Firma"
      >
        <UInput
          v-model="state.company"
          placeholder="Firmanavn"
          autocomplete="organization"
        />
      </UFormField>

      <UFormField
        name="vat_number"
        label="CVR-nummer"
      >
        <UInput
          v-model="state.vat_number"
          placeholder="CVR-nummer"
        />
      </UFormField>
    </div>

    <!-- Message section -->
    <p class="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoal)]/55 pt-5 pb-1 border-t border-[var(--color-warm-border)]/40">
      Din besked
    </p>

    <UFormField
      v-if="!props.subject"
      name="subject"
      label="Emne"
      required
    >
      <USelect
        v-model="state.subject"
        :items="subjectOptions"
        placeholder="Hvad drejer henvendelsen sig om?"
        value-key="value"
      />
    </UFormField>

    <UFormField
      name="message"
      label="Besked"
      required
    >
      <UTextarea
        v-model="state.message"
        placeholder="Skriv din besked…"
        :rows="5"
        autoresize
        :maxrows="12"
      />
    </UFormField>

    <div class="pt-4 flex flex-col items-center">
      <UButton
        type="submit"
        :loading="contactMutation.isPending.value"
        color="primary"
        size="lg"
        block
        class="rounded-full font-semibold max-w-sm"
      >
        {{ contactMutation.isPending.value ? 'Sender…' : 'Send besked' }}
      </UButton>
      <p class="text-xs text-center text-[var(--color-charcoal)]/40 mt-3">
        Vi svarer typisk inden for 1-2 hverdage
      </p>
    </div>
  </UForm>
</template>
